import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import {
  ActivityLevel,
  CreateWasteContainerDto,
  IsotopeDto,
  StorageLocationDto,
  UpdateWasteContainerDto,
  WasteContainerDto,
  WasteType,
} from '@services/dtos'
import { wasteContainerSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Alert, FormControlLabel, Grid, Switch } from '@mui/material'
import {
  createWasteContainer,
  updateWasteContainer,
} from '@state/reducers/wasteContainerReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import SelectField from '../inputs/SelectField'
import { ActivityLevelLabels, WasteTypeLabels } from '@utils/constants'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import StorageLocationForm from './StorageLocationForm'
import { getStorageLocations, setStorageLocationFilters } from '@state/reducers/storageLocationReducer'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { getIsotopes, setIsotopeFilters } from '@state/reducers/isotopeReducer'
import IsotopeForm from './IsotopeForm'
import { FormCrudHeader } from './FormCrudHeader'
import ServerAutocompleteMultiple from '../autocomplete/ServerAutocompleteMultiple'
import { toLower, trim } from 'lodash'

const WASTE_TYPE_OPTIONS =
  (Object.entries(WasteTypeLabels) as Array<[WasteType, string]>)
    .map(([value, label]) => ({ label, value }))
const ACTIVITY_LEVEL_OPTIONS =
  (Object.entries(ActivityLevelLabels) as Array<[string, string]>)
    .map(([value, label]) => ({ label, value }))

type Props = {
  editingWasteContainer?: WasteContainerDto | null
  onClose: () => void
  duplicateCase?: boolean
  defaultValues?: Partial<CreateWasteContainerDto>
  initialIsotopes?: IsotopeDto[]
}

const WasteContainerForm: React.FC<Props> = ({
  editingWasteContainer,
  onClose,
  duplicateCase,
  defaultValues: propDefaultValues,
  initialIsotopes,
}) => {
  const {
    watch,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<CreateWasteContainerDto>({
    defaultValues: editingWasteContainer
      ? {
          code: editingWasteContainer.code,
          label: editingWasteContainer.label,
          type: editingWasteContainer.type,
          activityLevel: editingWasteContainer.activityLevel,
          notes: editingWasteContainer.notes,
          storageLocationId: editingWasteContainer.storageLocation?.id,
          isotopeIds: editingWasteContainer.isotopes.map(({ id }) => id) ?? [],
          location: editingWasteContainer.location,
          tareKg: editingWasteContainer.tareKg,
          volumeL: editingWasteContainer.volumeL,
          isInfectious: editingWasteContainer.isInfectious ?? false,
        }
      : { isInfectious: false, ...propDefaultValues },
    resolver: yupResolver(wasteContainerSchema),
  })

  const dispatch = useAppDispatch()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    dispatch(getIsotopes())
    return () => {
      dispatch(setIsotopeFilters({ search: '', activityLevel: undefined }))
    }
  }, [dispatch])

  useEffect(() => {
    if (editingWasteContainer?.isotopes?.length) {
      for (const it of editingWasteContainer.isotopes) {
        itemCacheRef.current.set(it.id, it)
      }
    }
  }, [editingWasteContainer])

  const activityLevel = watch('activityLevel')
  const showIsotopes = activityLevel === ActivityLevel.LOW || activityLevel === ActivityLevel.HIGH

  useEffect(() => {
    if (!showIsotopes) {
      setValue('isotopeIds', [])
    }
  }, [showIsotopes])

  const normalize = (str: string) => toLower(trim(str))
  const isDuplicate = (duplicateCase && editingWasteContainer) && 
    (
      (editingWasteContainer.code && normalize(editingWasteContainer.code) === normalize(watch('code') ?? ''))
      || (normalize(editingWasteContainer.label) === normalize(watch('label') ?? ''))
    )

  const onSubmitForm = async (data: UpdateWasteContainerDto) => {
    if (isDuplicate) {
      return
    }
    setIsSubmitting(true)

    if (editingWasteContainer && !duplicateCase) {
      dispatch(updateWasteContainer(
        { 
          id: editingWasteContainer.id, 
          dto: 
            { 
              ...data,
              storageLocationId: data.storageLocationId ?? null,
            }
        })
      )
        .unwrap()
        .then(() => {
          cb('Le conteneur à déchets a été modifié avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue, vérifiez que le label n\'est pas déjà utilisé'))
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createWasteContainer(data as CreateWasteContainerDto))
        .unwrap()
        .then(() => {
          cb(`Le conteneur à déchets a été ${duplicateCase ? 'dupliqué' : 'créé'} avec succès`)
          onClose()
        })
        .catch(() => cb('Une erreur est survenue, vérifiez que le label n\'est pas déjà utilisé'))
        .finally(() => setIsSubmitting(false))
    }
  }

  const fields1 = [
    { name: 'code', label: 'Code', type: 'text', sm: 6 },
    { name: 'label', label: 'Label', type: 'text', sm: 6 },
  ]

  const fields2 = [
    { name: 'tareKg', label: 'Poids à vide (kg)', type: 'number', sm: 3 },
    { name: 'volumeL', label: 'Volume (L)', type: 'number', sm: 3 },
    { name: 'location', label: 'Emplacement', sm: 6 },
  ]

  const fields3 = [
    { name: 'notes', label: 'Notes', type: 'textarea', sm: 12 },
  ]

  const selects: { name: 'type' | 'activityLevel'; label: string; options: Array<{ label: string; value: string }> }[] = [
    { name: 'type', label: 'Type', options: WASTE_TYPE_OPTIONS },
    { name: 'activityLevel', label: 'Niveau d\'activité', options: ACTIVITY_LEVEL_OPTIONS },
  ]

  const fetchStorageLocations = async (search: string) => {
    dispatch(setStorageLocationFilters({ search }))
    const page = await dispatch(getStorageLocations()).unwrap()
    return page?.datas ?? []
  }

  const fetchIsotopes = async (search: string) => {
    dispatch(setIsotopeFilters({
      search,
      activityLevel: activityLevel !== ActivityLevel.NONE ? activityLevel : undefined,
    }))
    const page = await dispatch(getIsotopes()).unwrap()
    const list = page?.datas ?? []
    for (const it of list) {
      itemCacheRef.current.set(it.id, it)
    }
    return list
  }

  const renderFields = (fields: any[]) => {
    return fields.map((field) => (
      <Grid item xs={12} sm={field.sm ?? 6} key={field.name}>
        <InputField
          control={control}
          name={field.name}
          label={field.label}
          type={field.type ?? 'text'}
          multiline={field.type === 'textarea'}
          rows={field.type === 'textarea' ? 2 : undefined}
          {...(duplicateCase && editingWasteContainer && (
            field.name === 'code' && editingWasteContainer.code && normalize(editingWasteContainer.code) === normalize(watch('code') ?? '') ||
            field.name === 'label' && normalize(editingWasteContainer.label) === normalize(watch('label') ?? '')
          )
            ? { color: 'warning', focused: true }
            : {})}
        />
      </Grid>
    ))
  }

  const itemCacheRef = React.useRef(new Map<string, IsotopeDto>(
    (initialIsotopes ?? []).map((it) => [it.id, it]),
  ))

  return (
    <div className="flex w-full h-full p-4">
      <form onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }} className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingWasteContainer}
        />

        <div className="max-w-screen-md mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Conteneur à déchets
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              {isDuplicate && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    {`${editingWasteContainer?.code
                      ? 'Le code et le label doivent être différents du conteneur à déchets original.' 
                      : 'Le label doit être différent du conteneur à déchets original.'}`}
                  </Alert>
                </Grid>
              )}

              {renderFields(fields1)}

              {selects.map(({ name, label, options }) => (
                <Grid item xs={12} sm={6} key={name}>
                  <SelectField
                    key={name}
                    control={control}
                    name={name}
                    options={options}
                    value={watch(name)}
                    label={label}
                  />
                </Grid>
              ))}

              {renderFields(fields2)}

              <Grid item xs={12} sm={6}>
                <AddWrapperInput
                  legend="Stockage"
                  addTooltipTitle="Ajouter un stockage"
                  dialogContent={(close) => (
                    <StorageLocationForm
                    onClose={() => {
                      close()
                    }}
                  />
                )}
                onAfterClose={() => {
                  dispatch(getStorageLocations())
                }}
                input={(<ServerAutocomplete<StorageLocationDto>
                  fetchService={fetchStorageLocations}
                  onChange={(value: StorageLocationDto | null) => {
                    setValue('storageLocationId', value?.id)
                  }}
                  getValue={(value) => value?.id?.toString() || null}
                  getOptionLabel={(value) => {
                    const storageLocation = value as StorageLocationDto
                    return storageLocation ? storageLocation.name : ''
                  }}
                  debounceMs={300}
                  placeholder="Rechercher un stockage"
                  label="Stockage"
                  helperText={errors.storageLocationId?.message}
                  error={!!errors.storageLocationId}
                  minSearchLength={0}
                  initialOptions={editingWasteContainer?.storageLocation ? [editingWasteContainer.storageLocation] : []}
                  value={editingWasteContainer?.storageLocation ?? null}
                />)}
              />
              </Grid>

              {showIsotopes && (
                <Grid item xs={12} sm={6}>
                  <AddWrapperInput
                    legend="Isotopes"
                    addTooltipTitle="Ajouter un isotope"
                    dialogContent={(close) => (
                      <IsotopeForm
                        onClose={() => {
                          close()
                        }}
                      />
                    )}
                    onAfterClose={() => {
                      dispatch(getIsotopes())
                    }}
                    input={<Controller
                      name="isotopeIds"
                      control={control}
                      render={({ field: { value: ids = [], onChange } }) => {
                        const selected: IsotopeDto[] = ids
                          .map((id: string) => itemCacheRef.current.get(id))
                          .filter(Boolean) as IsotopeDto[]

                        return (
                          <ServerAutocompleteMultiple<IsotopeDto>
                            values={selected}
                            onChange={(isotopes) =>
                              onChange(isotopes.map(({ id }) => id))
                            }
                            fetchService={fetchIsotopes}
                            getOptionLabel={({ name, code }) => `${name} (${code})`}
                            label="Isotopes"
                            minSearchLength={1}
                            placeholder="Rechercher des isotopes..."
                            helperText={(errors?.isotopeIds as any)?.message}
                            error={!!errors?.isotopeIds}
                          />
                        )
                      }}
                    />}
                />
                </Grid>
              )}

              <Grid item xs={12}>
                <Controller
                  name="isInfectious"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={<Switch checked={!!value} onChange={(e) => onChange(e.target.checked)} color="warning" />}
                      label="DASRI"
                    />
                  )}
                />
              </Grid>

              {renderFields(fields3)}
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default WasteContainerForm
